// ============================================================================
// Simple SPA Router
// ============================================================================

import { escapeHtml } from 'shared/utils/dom-helpers.js';
import { EventManager } from 'shared/utils/event-manager.js';
import { state } from 'app/state/state.js';
import { isModuleLoadError } from 'app/shell/stale-chunk-guard.js';

const APP_NAME = 'AERIUM';

// The dashboard reads none of the shared dropdown/state stores, so it renders
// without waiting for them. Every other route does, so navigate() awaits
// state.init() before rendering it (instant on a warm cache; a single network
// round-trip on a cold one - same cost it has always paid, just no longer
// gating the dashboard's first paint at boot).
const STATE_INDEPENDENT_ROUTES = new Set(['/', '/dashboard']);

// Default page titles per route
const ROUTE_TITLES = {
    '/': 'Dashboard',
    '/dashboard': 'Dashboard',
    '/clients': 'Clients',
    '/client': 'Client',
    '/tasks': 'Tasks',
    '/timesheet': 'Timesheet',
    '/billing': 'Billing',
    '/settings': 'Settings',
    '/groups': 'Groups',
    '/lists': 'Lists',
    '/list-view': 'List',
    '/task': 'Task',
    '/reports': 'Reports',
    '/stats': 'Stats',
    '/calendar': 'Calendar',
    '/leave-calendar': 'Leave Calendar',
    '/activity': 'Activity',
    '/profile': 'Profile',
    '/retainers': 'Retainers',
    '/supplier-bills': 'Supplier Bills',
    '/sign-off': 'Sign-off',
    '/audit-planning': 'Audit Planning',
    '/portfolio': 'Portfolio',
    '/provisionals': 'Provisionals',
    '/drafter': 'Drafter',
    '/dev-tracker': 'Dev Tracker',
};

// Routes that should not have padding (full-width layouts)
const NO_PADDING_ROUTES = new Set(['/client', '/task']);

// Orphaned element selectors to clean up on navigation. Floating dropdowns/
// popovers are body-attached; grids close them via FloatingDropdown.closeAll()
// on hide(), so anything caught here is a leak worth the console warning.
const ORPHAN_SELECTORS = [
    '.tooltip', '.billing__tooltip', '.tasks__tooltip',
    '.dropdown-menu', '[class*="-floating-dropdown"]',
    '[class*="multiselect-dropdown"]', '[class*="__linked-tasks-dropdown"]',
    '[class*="__comments-popover"]', '[class*="__task-events-popover"]',
    '.tasks__date-picker', '.client__search-dropdown',
    '.audit-planning-popover'
].join(', ');

// Global window properties to clean up (prevents memory leaks)
// Note: Pages should clean up their own globals - this is a safety net
const WINDOW_CLEANUP_KEYS = [
    'currentClientId', 'servicesData', 'groupsData', 'notesUnsubscribe',
    'clientTimeouts'
];

class Router {
    constructor() {
        this.routes = {};
        this.currentModule = null;
        this.currentPath = null;
        this.currentFullHash = null;
        this.isNavigating = false;
        this.navigationQueue = null;
        this.events = new EventManager('Router');
        this.initialized = false;
    }

    // Extract path and query from hash
    parseHash(hash = window.location.hash) {
        const full = hash.startsWith('#') ? hash.slice(1) : hash || '/';
        const [path, query = ''] = full.split('?');
        return { full, path, query };
    }

    register(path, pageLoader) {
        this.routes[path] = pageLoader;
    }

    /**
     * Point the address bar at a hash without navigating, keeping the record
     * navigate() compares against in step. Every in-place rewrite comes through
     * here. A page that swaps its own content (the client profile's prev / next
     * and header search, the Portfolio tabs, the provisionals register opening
     * and closing a paper) would otherwise leave that record on the hash the
     * router last loaded, and navigate() would skip a later link back to it as
     * "already here".
     *
     * Only ever rewrites the CURRENT route's query. A link clicked while a
     * navigation is under way (the old page still on screen as the next one
     * loads, or the next one still in its init()) has already moved the address
     * bar on to its own route; a rewrite then would land on top of it, and the
     * router would carry the stray query onto that page and load it twice. So
     * the rewrite is dropped, and a page's own init() comes through here too
     * (the client profile giving a bare #/client its id).
     * @param {string} hash - e.g. "#/client?id=42"
     */
    replaceHash(hash) {
        if (this.parseHash(hash).path !== this.parseHash().path) return;
        window.history.replaceState(null, '', hash);
        this.currentFullHash = this.parseHash().full;
    }

    /**
     * Whether an exact path is a registered route. Used to reserve those paths
     * from being claimed as a custom-list slug alias (which would otherwise be
     * permanently shadowed by the real route).
     * @param {string} path - e.g. "/billing"
     * @returns {boolean}
     */
    isRegisteredPath(path) {
        return Object.prototype.hasOwnProperty.call(this.routes, path);
    }

    /**
     * Whether a path looks like a single-segment custom-list slug (e.g. "/afs").
     * Allows mixed case so #/AFS resolves (the lookup is case-insensitive), but
     * excludes the mixed-case form of a real route (e.g. #/Billing) so those keep
     * the plain "Page Not Found" instead of a pointless slug lookup + redirect.
     * @param {string} path
     * @returns {boolean}
     */
    isPotentialListSlug(path) {
        return /^\/[a-z0-9-]+$/i.test(path) && !this.isRegisteredPath(path.toLowerCase());
    }

    /**
     * Send a retired path to where its page moved to, carrying whatever the old
     * URL meant. A page that becomes a tab keeps its links working; without this
     * a single-segment retired path falls through isPotentialListSlug into the
     * list-view page and dead-ends on "list not found".
     *
     * @param {string} path
     * @returns {boolean} true if a redirect was issued (caller must stop)
     */
    applyRedirect(path) {
        if (path !== '/time-review') return false;
        // The report is the Portfolio's Time tab now. `?user=` was the person it
        // opened on, which the shell reads as `?person=`.
        const query = (window.location.hash.split('?')[1]) || '';
        const user = new URLSearchParams(query).get('user');
        const person = user ? `&person=${encodeURIComponent(user)}` : '';
        window.location.hash = `#/portfolio?view=time${person}`;
        return true;
    }

    async navigate(path) {
        // Queue navigation if already in progress
        if (this.isNavigating) {
            this.navigationQueue = path;
            return;
        }

        if (this.applyRedirect(path)) return;

        const { full: fullHash } = this.parseHash();

        // Skip if same path and hash (but allow query param changes)
        if (this.currentPath === path && fullHash === this.currentFullHash) {
            return;
        }

        this.isNavigating = true;
        const app = document.getElementById('app');

        try {
            // Disable margin-left transition during navigation to prevent
            // visual sliding when leaving pages that override margin (e.g. dashboard)
            app.style.transition = 'none';

            // Load page module first (while old page is still visible).
            // Friendly list-slug fallback: an unknown single-segment path (e.g.
            // #/afs) is treated as a custom task-list alias and handed to the
            // list-view page, which resolves the slug to the real list (or shows
            // its own not-found state). Registered routes always win, so a slug
            // can never shadow a real page.
            let pageLoader = this.routes[path];
            if (!pageLoader && this.isPotentialListSlug(path)) {
                pageLoader = this.routes['/list-view'];
            }
            if (!pageLoader) {
                app.innerHTML = `
                    <div class="app__error">
                        <i class="fas fa-map-signs app__error-icon"></i>
                        <h2 class="app__error-title">Page Not Found</h2>
                        <p class="app__error-message">The page "${escapeHtml(path)}" does not exist.</p>
                        <button onclick="window.location.hash='#/'" class="dashboard__button dashboard__button--primary">
                            <i class="fas fa-home dashboard__button-icon"></i> Go to Dashboard
                        </button>
                    </div>
                `;
                this.currentPath = path;
                this.currentFullHash = fullHash;
                return;
            }
            const _routeLoadStart = (path === '/task') ? Date.now() : 0;
            const pageModule = await pageLoader();
            if (_routeLoadStart) {
                // eslint-disable-next-line no-console
                console.log(`%c[task-perf] router: pageLoader() module import = ${Date.now() - _routeLoadStart}ms`, 'color:#7c3aed');
            }

            // Cleanup previous page (after module is ready so swap is instant)
            if (this.currentPath && this.currentModule) {
                const _cleanupStart = (path === '/task') ? Date.now() : 0;
                document.activeElement?.blur();
                await this.currentModule.hide?.();
                await this.currentModule.cleanup?.();
                this.cleanupOrphans();
                this.cleanupGlobals();
                if (_cleanupStart) {
                    // eslint-disable-next-line no-console
                    console.log(`%c[task-perf] router: previous-page cleanup = ${Date.now() - _cleanupStart}ms`, 'color:#7c3aed');
                }
            }

            this.currentModule = pageModule;

            // Ensure the shared dropdown/state stores are ready before any route
            // that consumes them renders. Started at boot and in-flight-guarded,
            // so this resolves immediately once warm; the page-chunk import above
            // overlapped the cold-cache fetch.
            if (!STATE_INDEPENDENT_ROUTES.has(path)) {
                await state.init();
            }

            // Toggle padding and swap to new page template in one step (no spinner gap)
            app.classList.toggle('app--no-padding', NO_PADDING_ROUTES.has(path));

            // Force reflow so layout changes apply instantly, then restore transitions
            app.style.transition = 'none';
            app.offsetHeight;

            // Set default page title BEFORE template/init so the browser tab
            // (and new-tab title) updates the moment the route is known, not
            // 200-500 ms later after init() resolves. Pages may overwrite via
            // setPageTitle() inside template() or init().
            // Context first, no "AERIUM" prefix - with many tabs open the prefix
            // is all that fits, so every tab would read identically. The route
            // (or dynamic) label is what tells tabs apart at a glance.
            document.title = ROUTE_TITLES[path] || APP_NAME;

            if (pageModule.template) {
                app.innerHTML = pageModule.template();
            }

            app.offsetHeight;
            app.style.transition = '';

            // Page init handles its own loading state. Capture the hash first so
            // we can tell whether init() deliberately redirected (e.g. a slug
            // route that resolved no list redirects to #/lists).
            const hashBeforeInit = this.parseHash().full;
            if (pageModule.init) {
                await pageModule.init();
            }

            // Update state - re-read hash in case init() used replaceState
            this.currentPath = path;
            this.currentFullHash = this.parseHash().full;

            // Padding class was already toggled before the spinner (above)

            // Sync URL if path changed (preserve query params). Skip if another
            // navigation is already queued (the user navigated away during async
            // init) OR if init() itself changed the hash - forcing it back would
            // revert a deliberate page redirect and strand the user.
            if (!this.navigationQueue) {
                const { full, path: urlPath, query } = this.parseHash();
                if (full === hashBeforeInit && urlPath !== path) {
                    window.location.hash = query ? `${path}?${query}` : path;
                }
            }

        } catch (error) {
            console.error('[Router] Navigation failed:', error);
            // A page chunk that cannot be fetched means the build moved on
            // under a long-lived session. Say that plainly instead of dumping
            // the raw import error. Worded to hold whether or not
            // staleChunkGuard auto-reloads (it holds off when the server is
            // unreachable), so the button is always the honest instruction.
            const staleBuild = isModuleLoadError(error);
            app.innerHTML = `
                <div class="app__error">
                    <i class="fas ${staleBuild ? 'fa-cloud-download-alt' : 'fa-exclamation-triangle'} app__error-icon"></i>
                    <h2 class="app__error-title">${staleBuild ? 'Update Available' : 'Page Load Error'}</h2>
                    <p class="app__error-message">${staleBuild
                        ? 'A newer version of AERIUM has been released, so this page is out of date.'
                        : escapeHtml(error.message)}</p>
                    <button onclick="location.reload()" class="dashboard__button dashboard__button--primary">Reload</button>
                </div>
            `;
        } finally {
            this.isNavigating = false;

            // Process queued navigation
            if (this.navigationQueue !== null) {
                const nextPath = this.navigationQueue;
                this.navigationQueue = null;
                // Allow DOM to settle before next navigation
                queueMicrotask(() => this.navigate(nextPath));
            }
        }
    }

    cleanupOrphans() {
        const orphans = document.querySelectorAll(ORPHAN_SELECTORS);
        if (orphans.length > 0) {
            console.warn(`[Router] Cleanup caught ${orphans.length} orphaned element(s):`,
                [...orphans].map(el => el.className || el.tagName)
            );
            orphans.forEach(el => el.remove());
        }
    }

    cleanupGlobals() {
        const found = [];

        // Clear timeouts first if they exist
        if (window.clientTimeouts) {
            found.push(`clientTimeouts (${window.clientTimeouts.size} items)`);
            window.clientTimeouts.forEach(id => clearTimeout(id));
            window.clientTimeouts.clear();
        }

        // Check which globals exist before deleting
        WINDOW_CLEANUP_KEYS.forEach(key => {
            if (window[key] !== undefined) {
                found.push(key);
                delete window[key];
            }
        });

        if (found.length > 0) {
            console.warn('[Router] Cleanup caught globals:', found);
        }
    }

    init() {
        if (this.initialized) return;
        this.initialized = true;

        // Register routes
        this.register('/', () => import('features/dashboard/page/dashboard-page.js'));
        this.register('/dashboard', () => import('features/dashboard/page/dashboard-page.js'));
        this.register('/clients', () => import('features/client-directory/page/clients-page.js'));
        this.register('/client', () => import('features/client-profile/page/client-page.js'));
        this.register('/tasks', () => import('features/tasks/page/tasks-page.js'));
        this.register('/timesheet', () => import('features/timesheet/page/timesheet-page.js'));
        this.register('/billing', () => import('features/billing/page/billing-page.js'));
        this.register('/settings', () => import('features/settings/page/settings-page.js'));
        this.register('/groups', () => import('features/groups/page/groups-page.js'));
        this.register('/lists', () => import('features/lists/page/lists-management-page.js'));
        this.register('/list-view', () => import('features/lists/page/task-list-view-page.js'));
        this.register('/task', () => import('features/tasks/page/task-view-page.js'));
        this.register('/reports', () => import('features/reports/page/reports-page.js'));
        this.register('/stats', () => import('features/stats/page/stats-page.js'));
        this.register('/calendar', () => import('features/calendar/page/calendar-page.js'));
        this.register('/leave-calendar', () => import('features/leave-calendar/page/leave-calendar-page.js'));
        this.register('/activity', () => import('features/user-activity/page/user-activity-page.js'));
        this.register('/profile', () => import('features/profile/page/profile-page.js'));
        this.register('/retainers', () => import('features/retainers/page/retainer-matrix-page.js'));
        this.register('/supplier-bills', () => import('features/supplier-bills/page/supplier-bills-page.js'));
        this.register('/dev-tracker', () => import('features/dev-tracker/page/dev-tracker-page.js'));
        this.register('/sign-off', () => import('features/sign-off/page/sign-off-page.js'));
        this.register('/audit-planning', () => import('features/audit-planning/page/audit-planning-page.js'));
        this.register('/portfolio', () => import('features/portfolio/page/portfolio-page.js'));
        this.register('/provisionals', () => import('features/provisionals/page/provisionals-page.js'));
        this.register('/drafter', () => import('features/tb-drafter/page/tb-drafter-page.js'));

        // Handle hash changes
        this.events.on(window, 'hashchange', () => {
            this.navigate(this.parseHash().path);
        });

        // Handle navigation clicks
        this.events.on(document, 'click', (e) => {
            const link = e.target.closest('[data-route]');
            if (!link) return;

            e.preventDefault();

            if (link.href?.includes('#')) {
                window.location.hash = link.href.split('#')[1] || '/';
            } else {
                this.navigate(link.dataset.route);
            }
        });

        // Initial navigation
        this.navigate(this.parseHash().path);
    }

    /**
     * Tear down router listeners. Not currently called in normal app flow
     * (router lives for the session) but provided for completeness and testability.
     */
    dispose() {
        this.events.dispose();
        this.initialized = false;
    }
}

export const router = new Router();

/**
 * Set a specific page title (for dynamic pages like client profiles, tasks, lists).
 * Context only, no "AERIUM" prefix - see the navigate() note above. Falls back to
 * the app name only when there is nothing contextual to show.
 * @param {string} title - The specific title to display (e.g. client name)
 */
export function setPageTitle(title) {
    document.title = title || APP_NAME;
}
